import { Badge, Group, Stack, Text } from '@mantine/core';
import { IconBriefcase, IconPaperclip } from '@tabler/icons-react';
import { createColumnHelper } from '@tanstack/react-table';
import { ClientHoverCard } from 'components/ClientHoverCard';
import { i18nKeys } from 'locales';
import { useTranslation } from 'locales/logic';
import type { LightThread } from 'modules/Mails/hooks/use-load-mails';
import { MAIL_CATEGORY_COLORS, MAIL_CATEGORY_LABELS } from 'modules/Mails/types/mail-category';
import { useMemo } from 'react';
import { isNonNull } from 'remeda';
import { useUpdateThread } from 'shared/hooks/use-update-thread';
import { MailCheckboxCell } from '../components/MailCheckboxCell';
import { MailDateCell } from '../components/MailDateCell';

const columnHelper = createColumnHelper<LightThread>();

export const useBuildMailColumns = (showCategories: boolean) => {
  const { updateThread } = useUpdateThread();
  const { t } = useTranslation();

  return useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        size: 50,
        cell: info => {
          const { isProcessed } = info.row.original;
          const { id } = info.row.original;
          const toggleUpdateThread = () =>
            updateThread({
              id,
              isProcessed: !isProcessed,
            });

          return (
            <MailCheckboxCell
              row={info.row}
              isSelected={info.row.getIsSelected()}
              toggleUpdateThread={toggleUpdateThread}
            />
          );
        },
      }),
      columnHelper.accessor('mailDetail.fromMail', {
        size: 90,
        cell: info => {
          const { fromMail, fromName, read } = info.row.original.mailDetail;
          const { replyCount } = info.row.original;

          return (
            <Stack gap={2}>
              <Group align="center" gap={4} wrap="nowrap">
                <Text size="sm" fw={read ? undefined : 700} truncate="end">
                  {fromName ?? fromMail}
                </Text>
                {replyCount > 0 && (
                  <Text fw={600} c="coral" size="xs" style={{ flexShrink: 0 }}>
                    {replyCount}
                  </Text>
                )}
              </Group>
            </Stack>
          );
        },
      }),
      columnHelper.accessor('mailDetail.subject', {
        size: 350,
        cell: info => {
          const { subject, hasAttachments, read, bodyPreview, categories } =
            info.row.original.mailDetail;
          const { accountManager, hasDraft } = info.row.original;
          const hasAccountManager = isNonNull(accountManager);

          return (
            <Stack gap={2}>
              <Group gap={2} wrap="nowrap">
                {hasDraft && (
                  <Text size="sm" fw={700} c="red" style={{ flexShrink: 0 }}>
                    {t(i18nKeys.EXTERNAL_MAIL.DRAFT)}
                  </Text>
                )}
                <Text
                  size="sm"
                  fw={read ? 600 : 700}
                  truncate="end"
                  style={{ flex: '0 1 auto', minWidth: 0 }}
                >
                  {subject}
                </Text>
                {!hasAccountManager && (
                  <Text size="sm" c="dimmed" truncate="end" style={{ flex: '1 0 0', minWidth: 0 }}>
                    {`- ${bodyPreview}`}
                  </Text>
                )}
                {showCategories && categories.length > 0 && hasAccountManager && (
                  <Group ml={'sm'} gap={4} wrap="wrap">
                    {categories.map(category => (
                      <Badge key={category} variant="light" size="sm">
                        {t(MAIL_CATEGORY_LABELS[category])}
                      </Badge>
                    ))}
                  </Group>
                )}
                {hasAttachments && (
                  <IconPaperclip stroke={1.5} size={14} style={{ flexShrink: 0 }} />
                )}
              </Group>
              {hasAccountManager && (
                <Text size="sm" c="dimmed" truncate="end">
                  {bodyPreview}
                </Text>
              )}
              {showCategories && categories.length > 0 && !hasAccountManager && (
                <Group gap={4} wrap="wrap">
                  {categories.map(category => (
                    <Badge
                      color={MAIL_CATEGORY_COLORS[category]}
                      key={category}
                      variant="light"
                      size="sm"
                    >
                      {t(MAIL_CATEGORY_LABELS[category])}
                    </Badge>
                  ))}
                </Group>
              )}
            </Stack>
          );
        },
      }),
      columnHelper.display({
        id: 'debtor',
        size: 70,
        cell: info => {
          const { debtor, accountManager } = info.row.original;

          return (
            <Stack gap={0} style={{ minWidth: 0 }}>
              {debtor?.id && debtor.fullName && (
                <ClientHoverCard debtorId={debtor.id} debtorName={debtor.fullName} />
              )}
              {accountManager && (
                <Group gap={2} align="center" wrap="nowrap">
                  <IconBriefcase stroke={1.5} size={12} style={{ flexShrink: 0 }} />
                  <Text
                    size="xs"
                    c="dimmed"
                    style={{ whiteSpace: 'nowrap' }}
                  >{`${accountManager?.name.full}.`}</Text>
                </Group>
              )}
            </Stack>
          );
        },
      }),
      columnHelper.accessor('mailDetail.date', {
        size: 50,
        cell: info => {
          const rowBg = info.row.getIsSelected()
            ? 'var(--mantine-color-blue-1)'
            : !info.row.original.mailDetail.read
              ? 'var(--mantine-color-purple-1)'
              : undefined;
          return <MailDateCell mail={info.row.original} rowBg={rowBg} />;
        },
      }),
    ],
    [updateThread, showCategories],
  );
};
